import { createContext } from 'react'
import { ELEMENT_TYPES } from 'cg/components/documents/editor/slate/types'
import { Transforms, Path, Editor, Point, Range, Node, Element as SlateElement } from 'vendor/slate'
import { trimTabsAndNewLines } from 'cg/components/documents/helpers'
import { ReactEditor } from 'vendor/slate-react'
import { v4 as uuidv4 } from 'uuid'

const DEFAULT_INDENTATION = '  '

const CodeTabsDispatch = createContext(null)
const codeTabsReducer = (state, action) => {
  switch (action.type) {
    case 'setActiveCodeTab':
      return { activeCodeTab: action.data }
    default:
      return state
  }
}

const insertCodeTabs = (editor, path) => {
  const id = `code-${uuidv4()}`
  const firstTabId = `code-${uuidv4()}-1`

  const codeTabs = {
    id,
    type: ELEMENT_TYPES.code_tabs,
    defaultActiveTab: firstTabId,
    children: [
      {
        type: ELEMENT_TYPES.code_tabs_header,
        parent: { type: ELEMENT_TYPES.code_tabs },
        children: [
          {
            type: ELEMENT_TYPES.code_tabs_header_item,
            parent: { type: ELEMENT_TYPES.code_tabs_header },
            tabId: firstTabId,
            children: [{ uuid: uuidv4(), text: 'Title', parent: { type: ELEMENT_TYPES.code_tabs_header_item } }]
          }
        ]
      },
      {
        type: ELEMENT_TYPES.code_tabs_content_item,
        tabId: firstTabId,
        parent: { type: ELEMENT_TYPES.code_tabs },
        language: 'plaintext',
        children: [
          {
            type: ELEMENT_TYPES.code_tabs_content_item_line,
            parent: { type: ELEMENT_TYPES.code_tabs_content_item },
            children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.code_tabs_content_item_line } }]
          }
        ]
      }
    ]
  }

  Transforms.insertNodes(editor, codeTabs, { at: path, select: true })
  ReactEditor.focus(editor)
  return firstTabId
}

const insertCodeTab = (editor, codeTabHeaderPath, codeTabContentPath) => {
  const firstTabId = `code-${uuidv4()}`
  const codeTabHeaderItem = {
    type: ELEMENT_TYPES.code_tabs_header_item,
    tabId: firstTabId,
    parent: { type: ELEMENT_TYPES.code_tabs_header },
    children: [{ uuid: uuidv4(), text: 'Title', parent: { type: ELEMENT_TYPES.code_tabs_header_item } }]
  }
  const codeTabContentItem = {
    type: ELEMENT_TYPES.code_tabs_content_item,
    tabId: firstTabId,
    parent: { type: ELEMENT_TYPES.code_tabs },
    language: 'plaintext',
    children: [
      {
        type: ELEMENT_TYPES.code_tabs_content_item_line,
        parent: { type: ELEMENT_TYPES.code_tabs_content_item },
        children: [{ uuid: uuidv4(), text: '', parent: { type: ELEMENT_TYPES.code_tabs_content_item_line } }]
      }
    ]
  }

  Transforms.insertNodes(editor, codeTabHeaderItem, { at: Path.next(codeTabHeaderPath) })
  Transforms.insertNodes(editor, codeTabContentItem, { at: Path.next(codeTabContentPath) })
}

const firstDifferentCharacter = (a, b) => {
  if (a.length > b.length) {
    return firstDifferentCharacter(b, a)
  }

  const indexes = Array(a.length)
    .fill()
    .map((v, i) => i)
  const index = indexes.find(i => a[i] !== b[i])

  return index == null ? a.length : index
}

const getSelectedElements = editor => {
  const { selection } = editor
  const elements = []
  if (selection) {
    const [start, end] = Range.edges(selection)
    const startPath = start.path.slice(0, -1)
    const endPath = end.path.slice(0, -1)

    for (let i = startPath[startPath.length - 1]; i <= endPath[endPath.length - 1]; i++) {
      const path = startPath.slice(0, -1).concat(i)
      const node = Node.has(editor, path) ? Node.get(editor, path) : null
      if (node && node.type === ELEMENT_TYPES.code_tabs_content_item_line) {
        elements.push({ node, path })
      }
    }
  }
  return elements
}

const dedentLines = (editor, insertShiftTab) => {
  const { selection } = editor
  const { anchor, focus } = selection
  if (selection) {
    const match = Editor.nodes(editor, {
      at: selection,
      mode: 'lowest',
      match: n => !Editor.isEditor(n) && n.parent && n.parent.type === ELEMENT_TYPES.code_tabs_content_item_line
    })
    if (match) {
      const [node] = match
      if (node) {
        const elements = getSelectedElements(editor)
        elements.forEach(element => {
          for (const [node] of Node.texts(element.node)) {
            const dedentLength = firstDifferentCharacter(node.text, DEFAULT_INDENTATION)
            if (dedentLength > 0 && node && node.text) {
              const dedentedText = node.text.slice(dedentLength, node.text.length)
              const dedentedNode = {
                type: ELEMENT_TYPES.code_tabs_content_item_line,
                parent: { type: ELEMENT_TYPES.code_tabs_content_item },
                children: [{ text: dedentedText, parent: { type: ELEMENT_TYPES.code_tabs_content_item_line } }]
              }
              Transforms.removeNodes(editor, { at: element.path })
              Transforms.insertNodes(editor, dedentedNode, { at: element.path })
              if (Range.isCollapsed(selection)) {
                const offset = anchor.offset - dedentLength >= 0 ? anchor.offset - dedentLength : 0
                Transforms.select(editor, { anchor: { path: anchor.path, offset }, focus: { path: focus.path, offset } })
                return
              }
              Transforms.select(editor, {
                anchor: {
                  path: anchor.path,
                  offset: anchor.offset - DEFAULT_INDENTATION.length >= 0 ? anchor.offset - DEFAULT_INDENTATION.length : 0
                },
                focus: {
                  path: focus.path,
                  offset: focus.offset - DEFAULT_INDENTATION.length >= 0 ? focus.offset - DEFAULT_INDENTATION.length : 0
                }
              })
            }
          }
        })
        return
      }
    }
  }
  insertShiftTab()
}

const indentLines = (editor, insertTab) => {
  const { selection, insertText } = editor
  const { anchor, focus } = selection

  if (selection) {
    const match = Editor.nodes(editor, {
      at: selection,
      mode: 'lowest',
      match: n => !Editor.isEditor(n) && n.parent && n.parent.type === ELEMENT_TYPES.code_tabs_content_item_line
    })

    if (match) {
      const [node] = match
      if (node) {
        const elements = getSelectedElements(editor)
        if (Range.isCollapsed(selection)) {
          insertText(DEFAULT_INDENTATION)
          return
        }
        elements.forEach(element => {
          for (const [node] of Node.texts(element.node)) {
            const indentedText = DEFAULT_INDENTATION + node.text
            const indentedNode = {
              type: ELEMENT_TYPES.code_tabs_content_item_line,
              parent: { type: ELEMENT_TYPES.code_tabs_content_item },
              children: [{ text: indentedText, parent: { type: ELEMENT_TYPES.code_tabs_content_item_line } }]
            }
            Transforms.removeNodes(editor, { at: element.path })
            Transforms.insertNodes(editor, indentedNode, { at: element.path })
            Transforms.select(editor, {
              anchor: { path: anchor.path, offset: anchor.offset + DEFAULT_INDENTATION.length },
              focus: { path: focus.path, offset: focus.offset + DEFAULT_INDENTATION.length }
            })
          }
        })
        return
      }
    }
  }
  insertTab()
}

const withCodeTabs = editor => {
  const {
    insertBreak,
    deleteBackward,
    deleteForward,
    deleteFragment,
    insertText,
    insertData,
    insertTab,
    insertShiftTab,
    normalizeNode
  } = editor

  editor.normalizeNode = entry => {
    const [node, path] = entry
    if (node.type === ELEMENT_TYPES.code_tabs_content_item) {
      for (const [child, childPath] of Node.children(editor, path)) {
        for (const [element, elementPath] of Node.elements(child)) {
          if (element.type !== ELEMENT_TYPES.code_tabs_content_item_line) {
            Transforms.removeNodes(editor, { at: childPath.concat(elementPath) })
          }
        }

        if (child.type !== ELEMENT_TYPES.code_tabs_content_item_line) {
          let text = ''
          for (const [original] of Node.texts(child)) {
            text += original.text
          }
          Transforms.removeNodes(editor, { at: childPath })
          Transforms.insertNodes(
            editor,
            {
              type: ELEMENT_TYPES.code_tabs_content_item_line,
              parent: {
                type: child.parent.type
              },
              children: [{ text, parent: { type: ELEMENT_TYPES.code_tabs_content_item_line } }]
            },
            { at: childPath }
          )
          return
        }
      }
    }

    normalizeNode(entry)
  }

  // Prevent breaks on tab header item
  editor.insertBreak = () => {
    const { selection } = editor
    if (selection) {
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n =>
          (!Editor.isEditor(n) && n.parent && n.parent.type === ELEMENT_TYPES.code_tabs_header_item) ||
          (n.parent && n.parent.type === ELEMENT_TYPES.code_tabs_content_item)
      })
      if (match) {
        const [node] = match
        if (node) {
          const { parent } = node
          if (parent && parent.type === ELEMENT_TYPES.code_tabs_content_item) {
            Transforms.splitNodes(editor, { always: true })
          }
          return
        }
      }
    }

    insertBreak()
  }

  // Prevent node deletion on tab content and header items
  editor.deleteBackward = unit => {
    const { selection } = editor
    if (selection && Range.isCollapsed(selection)) {
      const { anchor } = selection
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n =>
          !Editor.isEditor(n) &&
          [ELEMENT_TYPES.code_tabs_content_item, ELEMENT_TYPES.code_tabs_header_item].includes(n.parent && n.parent.type)
      })

      if (match) {
        const [, path] = match
        if (Point.equals(anchor, Editor.start(editor, Path.parent(path)))) {
          return
        }
      }
    }

    deleteBackward(unit)
  }

  // Prevent node deletion on tab content and header items
  editor.deleteForward = unit => {
    const { selection } = editor

    if (selection && Range.isCollapsed(selection)) {
      const { anchor } = selection
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n =>
          !Editor.isEditor(n) &&
          [ELEMENT_TYPES.code_tabs_content_item, ELEMENT_TYPES.code_tabs_header_item].includes(n.parent && n.parent.type)
      })

      if (match) {
        const [node, path] = match
        if (node) {
          const end = Editor.end(editor, Path.parent(path))

          if (Point.equals(anchor, end)) {
            return
          }
        }
      }
    }

    deleteForward(unit)
  }

  editor.deleteFragment = () => {
    const { selection } = editor

    if (selection) {
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n =>
          !Editor.isEditor(n) &&
          [ELEMENT_TYPES.code_tabs_header_item, ELEMENT_TYPES.code_tabs_content_item_line].includes(n.parent && n.parent.type)
      })

      if (match) {
        const [start, end] = Range.edges(selection)
        if (start.path.length !== end.path.length) return
        const startPathNode = Node.parent(editor, start.path)
        const endPathNode = Node.parent(editor, end.path)

        if (startPathNode.parent && endPathNode.parent && startPathNode.parent.type !== endPathNode.parent.type) return
      }
    }
    deleteFragment()
  }

  editor.insertText = text => {
    const { selection } = editor
    if (selection) {
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n =>
          !Editor.isEditor(n) &&
          [ELEMENT_TYPES.code_tabs_header_item, ELEMENT_TYPES.code_tabs_content_item_line].includes(n.parent && n.parent.type)
      })

      if (match) {
        const [start, end] = Range.edges(selection)
        if (start.path.length !== end.path.length) return
        const startPathNode = Node.parent(editor, start.path)
        const endPathNode = Node.parent(editor, end.path)

        if (startPathNode.parent && endPathNode.parent && startPathNode.parent.type !== endPathNode.parent.type) return
      }
    }

    insertText(text)
  }

  editor.insertData = data => {
    const { selection } = editor

    if (selection) {
      const [match] = Editor.nodes(editor, {
        at: selection,
        mode: 'lowest',
        match: n =>
          !Editor.isEditor(n) &&
          [ELEMENT_TYPES.code_tabs_header_item, ELEMENT_TYPES.code_tabs_content_item_line].includes(n.parent && n.parent.type)
      })

      if (match) {
        const [start, end] = Range.edges(selection)
        if (start.path.length !== end.path.length) return
        const startPathNode = Node.parent(editor, start.path)
        const endPathNode = Node.parent(editor, end.path)

        if (startPathNode.parent && endPathNode.parent && startPathNode.parent.type !== endPathNode.parent.type) return

        const [node] = match
        if (node && node.parent && node.parent.type === ELEMENT_TYPES.code_tabs_header_item) {
          insertText(trimTabsAndNewLines(data.getData('text/plain')))
          return
        }
      }
    }

    insertData(data)
  }

  editor.insertTab = () => {
    indentLines(editor, insertTab)
  }

  editor.insertShiftTab = () => {
    dedentLines(editor, insertShiftTab)
  }

  return editor
}

export { insertCodeTabs, insertCodeTab, withCodeTabs, codeTabsReducer, CodeTabsDispatch }
